import { useCallback, useEffect, useRef } from 'react'
import type { DriveStep } from 'driver.js'
import { driver } from 'driver.js'
import 'driver.js/dist/driver.css'
import '../styles/driver-overrides.css'
import { api } from '@/lib/api'
import { useUser } from '@/contexts/UserContext'
import { SHARED_DRIVER_CONFIG } from '@/lib/guide-driver-config'

interface UseGuideOptions {
  /**
   * Fábrica de pasos invocada en tiempo de arranque (lazy).
   * Al llamarse en startGuide() los closures capturan el estado vivo del
   * componente — elimina el problema de closures obsoletos de useTour.
   */
  buildSteps: () => DriveStep[]
}

interface UseGuideResult {
  /**
   * Inicia la guía. Estable entre renders (useCallback) — seguro para pasarlo
   * a usePendingGuide como dependencia.
   */
  startGuide: () => void
}

/**
 * Hook de guía para módulos.
 *
 * Igual que useTour pero sin instancia persistida:
 * - Crea una instancia fresca de driver.js en cada llamada a startGuide()
 *   para garantizar closures actualizados.
 * - Al cerrar (X o paso final), llama POST /me/tours/{guideId}/visto y
 *   refresca el usuario desde la API (igual que useTour.onDestroyed).
 *   Un fallo del POST nunca rompe la página.
 * - La guardia markedRef evita POSTs duplicados si driver dispara onDestroyed
 *   más de una vez en la misma sesión de guía.
 *
 * IMPORTANTE: `buildSteps` debe ser estable (useCallback en el componente
 * que llama al hook) para que `startGuide` no se recree en cada render.
 */
export function useGuide(guideId: string, { buildSteps }: UseGuideOptions): UseGuideResult {
  const { refreshUser } = useUser()

  // Guardia: solo ejecutamos el POST una vez por sesión de guía.
  // Se reinicia en cada llamada a startGuide() para permitir el replay manual.
  const markedRef = useRef(false)

  // Referencia a la instancia activa para poder destruirla si la página se
  // desmonta (ej. el usuario navega desde el menú mientras la guía está abierta).
  const activeDriverRef = useRef<ReturnType<typeof driver> | null>(null)

  const startGuide = useCallback(() => {
    // Reiniciar guardia al arrancar (permite replay ilimitado sin acumular POSTs).
    markedRef.current = false

    const obj = driver({
      ...SHARED_DRIVER_CONFIG,
      steps: buildSteps(),
      onDestroyed: () => {
        if (markedRef.current) return
        markedRef.current = true
        // Fire-and-forget: un fallo de red no debe interrumpir la UX.
        ;(async () => {
          try {
            await api.post(`/me/tours/${guideId}/visto`)
            await refreshUser()
          } catch {
            // Fallo silencioso: la guía volverá a mostrarse en la próxima visita.
          }
        })()
      },
    })
    activeDriverRef.current = obj
    obj.drive()
  }, [guideId, buildSteps, refreshUser])

  // Destruir la guía activa si la página se desmonta (navegación), para no
  // dejar el overlay huérfano. isActive() evita destruir de más en StrictMode.
  useEffect(() => {
    return () => {
      if (activeDriverRef.current?.isActive()) activeDriverRef.current.destroy()
    }
  }, [])

  return { startGuide }
}
